/**
 * ArchitectureTab - 2-column data flow diagram + engine status block.
 *
 * PROD-644 / Sprint 3.1 alignment. Rebuilt to match the canonical prototype
 * at https://ai-native-prototype.vercel.app/care-pathway-studio.html
 * (Architecture tab section, verified 2026-05-07).
 *
 * Brand-Patch v2 canon: user-facing strings use the clinical pathway names
 * ("Cardiometabolic Pathway"); internal version ids (`tlp1-glp1-v1`) are
 * preserved per HC9 strict registry but never surface in display copy.
 *
 * Layout:
 *   - Full-width header: "Clinical AI Architecture for the Cardiometabolic Pathway"
 *   - 2-column flow diagram:
 *       Left  = Stream 1: Patient-Reported Data (5 sequential steps)
 *       Right = Stream 2: Clinical Event Data   (5 sequential steps)
 *   - Bottom: 5 engine cards (Red Flag / Pathway / LLM / NBA Card / JITAI)
 *     with status badges (EXISTS or TO BUILD) and emoji icons.
 *     LLM card includes a JSON schema code block.
 *
 * Plan Mode v9 ss4.1 (prototype = canonical source; ADR-001 footnote removed
 * per sprint instruction).
 *
 * HC2: every color, spacing, radius token resolves through Signal DS.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: 2-column flow has semantic regions (aria-labelledby per stream);
 *   each engine card has aria-labelledby pointing at its h3 heading.
 *
 * Validation tag: [IMPLEMENTED]
 */
// ---------------------------------------------------------------------------
// Stream step model
// ---------------------------------------------------------------------------

interface FlowStep {
  /** Stable test-id slug within its stream. */
  id: string;
  /** Primary label. */
  label: string;
  /** Secondary detail line (optional). */
  detail?: string;
  /** Prototype status indicator. */
  status?: 'exists' | 'to-build';
  /** Inline status note shown next to the status badge (optional). */
  statusNote?: string;
}

// ---------------------------------------------------------------------------
// Engine card model
// ---------------------------------------------------------------------------

type EngineStatus = 'exists' | 'to-build';

interface EngineCard {
  /** Stable test-id slug. */
  id: string;
  /** Left-column emoji icon. */
  emoji: string;
  /** Engine title. */
  title: string;
  /** One-line description. */
  description: string;
  /** Build status. */
  status: EngineStatus;
  /** Short note about where it lives. */
  statusNote?: string;
  /** If true, renders the LLM structured-output JSON schema block. */
  showJsonBlock?: boolean;
}

// ---------------------------------------------------------------------------
// Data: Stream 1 -- Patient-Reported Data
// ---------------------------------------------------------------------------

const STREAM1_STEPS: readonly FlowStep[] = [
  {
    id: 'prom-dispatch',
    label: 'Patient receives PROM link',
    detail: 'SMS / WhatsApp / in-app -- 48h before visit',
  },
  {
    id: 'health-bot-admin',
    label: 'Azure Health Bot administers instrument',
    detail: 'Adaptive Card screens -- PHQ-9, SF-12, MSQ, GSRS',
  },
  {
    id: 'scoring',
    label: 'Scrips.Scoring computes score',
    detail: 'PROM Engine microservice -- 119 validated algorithms',
    status: 'to-build',
    statusNote: '1 week effort',
  },
  {
    id: 'fhir-write',
    label: 'FHIR write: QuestionnaireResponse + Observation',
    detail: 'Trend delta computed vs prior score',
  },
  {
    id: 'event-bus-s1',
    label: 'IEventBus -- ClinicalDataChangedEvent',
    detail: 'RabbitMQ (primary) + Azure Service Bus',
    status: 'exists',
    statusNote: 'Scrips.Common/IEventBus',
  },
] as const;

// ---------------------------------------------------------------------------
// Data: Stream 2 -- Clinical Event Data
// ---------------------------------------------------------------------------

const STREAM2_STEPS: readonly FlowStep[] = [
  {
    id: 'clinical-action',
    label: 'Clinical action fires in encounter',
    detail: 'New diagnosis -- Medication started -- Lab resulted -- Vital recorded',
  },
  {
    id: 'cqrs-dispatch',
    label: 'MediatR CQRS dispatches command',
    detail: 'EvaluatePathwayCommand -- 36+ commands follow this pattern',
    status: 'exists',
    statusNote: 'all services',
  },
  {
    id: 'event-bus-s2',
    label: 'IEventBus -- ClinicalDataChangedEvent',
    detail: 'Same bus -- same event type -- same consumer',
    status: 'exists',
    statusNote: 'shared with Stream 1',
  },
  {
    id: 'signalr-broadcast',
    label: 'SignalR broadcasts NBA Card to encounter',
    detail: 'EncounterChangesNotificationHub -- already wired',
    status: 'exists',
    statusNote: 'Scrips.Patient/',
  },
  {
    id: 'clinician-approval',
    label: 'Clinician approves or overrides',
    detail: 'FHIR Provenance written -- full audit trail',
  },
] as const;

// ---------------------------------------------------------------------------
// Data: Engine cards
// ---------------------------------------------------------------------------

const ENGINE_CARDS: readonly EngineCard[] = [
  {
    id: 'red-flag-engine',
    emoji: '🚨',
    title: 'Red Flag Engine',
    description: 'Deterministic hardcoded rules. Fires BEFORE any LLM.',
    status: 'exists',
    statusNote: 'Scrips.RuleEngine/',
  },
  {
    id: 'pathway-engine',
    emoji: '⚙️',
    title: 'Pathway Engine',
    description:
      'Executes Care Pathway step sequences with timers, wait states, and branching.',
    status: 'exists',
    statusNote: 'Scrips.Workflows/',
  },
  {
    id: 'llm-layer',
    emoji: '🧠',
    title: 'LLM Layer',
    description:
      'Receives structured patient context. Returns structured JSON only -- never free text.',
    status: 'exists',
    statusNote: 'Scrips.Master/',
    showJsonBlock: true,
  },
  {
    id: 'nba-card-clinician',
    emoji: '👨‍⚕️',
    title: 'NBA Card -- Clinician',
    description: 'ClinicalDesk output. Presented to clinician for review.',
    status: 'to-build',
  },
  {
    id: 'jitai-patient',
    emoji: '📱',
    title: 'JITAI -- Patient',
    description:
      'Consumer app output. Just-In-Time Adaptive Intervention delivered to patient.',
    status: 'to-build',
  },
] as const;

// ---------------------------------------------------------------------------
// LLM structured-output JSON schema (prototype-canonical)
// ---------------------------------------------------------------------------

const LLM_JSON_SCHEMA = `{
  "next_best_action": "[action description]",
  "confidence": "[percentage]",
  "risk_tier": "[low/moderate/urgent]",
  "reasoning": "[≤200 chars]",
  "clinician_review_required": true
}`;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusBadge({
  status,
  note,
}: {
  status: EngineStatus;
  note?: string;
}) {
  if (status === 'exists') {
    return (
      <span className="inline-flex items-center gap-1 rounded-signal-pill bg-status-active-bg px-gutter-sm py-0.5 text-signal-xs font-semibold text-status-active-fg ring-1 ring-inset ring-status-active">
        <span aria-hidden="true">&#10003;</span>
        {note ? `EXISTS -- ${note}` : 'EXISTS'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-signal-pill bg-warning-strong-bg px-gutter-sm py-0.5 text-signal-xs font-semibold text-warning-strong-fg ring-1 ring-inset ring-warning-strong-border">
      <span aria-hidden="true">&#9888;</span>
      {note ? `TO BUILD -- ${note}` : 'TO BUILD'}
    </span>
  );
}

function StepStatusBadge({
  status,
  note,
}: {
  status: 'exists' | 'to-build';
  note?: string;
}) {
  if (status === 'exists') {
    return (
      <span className="mt-0.5 inline-flex shrink-0 items-center gap-0.5 rounded-signal-pill bg-status-active-bg px-gutter-sm py-0.5 text-signal-xs font-semibold text-status-active-fg ring-1 ring-inset ring-status-active">
        <span aria-hidden="true">&#10003;</span>
        {note ? `EXISTS -- ${note}` : 'EXISTS'}
      </span>
    );
  }
  return (
    <span className="mt-0.5 inline-flex shrink-0 items-center gap-0.5 rounded-signal-pill bg-warning-strong-bg px-gutter-sm py-0.5 text-signal-xs font-semibold text-warning-strong-fg ring-1 ring-inset ring-warning-strong-border">
      <span aria-hidden="true">&#9888;</span>
      {note ? `TO BUILD -- ${note}` : 'TO BUILD'}
    </span>
  );
}

function StepArrow() {
  return (
    <div aria-hidden="true" className="flex justify-center py-0.5">
      <svg
        width="8"
        height="12"
        viewBox="0 0 8 12"
        fill="currentColor"
        className="text-text-tertiary"
        aria-hidden="true"
      >
        <polygon points="4,12 0,7 8,7" />
      </svg>
    </div>
  );
}

function FlowStepCard({ step, isLast }: { step: FlowStep; isLast: boolean }) {
  return (
    <div data-testid={`flow-step-${step.id}`}>
      <div className="rounded-signal-md border border-surface-border bg-surface-base p-gutter-md shadow-signal-card">
        <p className="text-signal-sm font-medium text-text-primary">{step.label}</p>
        {step.detail && (
          <p className="mt-0.5 text-signal-xs text-text-secondary">{step.detail}</p>
        )}
        {step.status && (
          <div className="mt-gutter-sm">
            <StepStatusBadge status={step.status} note={step.statusNote} />
          </div>
        )}
      </div>
      {!isLast && <StepArrow />}
    </div>
  );
}

function StreamColumn({
  label,
  streamId,
  steps,
}: {
  label: string;
  streamId: string;
  steps: readonly FlowStep[];
}) {
  const headingId = `stream-heading-${streamId}`;
  return (
    <section
      aria-labelledby={headingId}
      data-testid={`stream-column-${streamId}`}
      className="flex flex-col"
    >
      <h3
        id={headingId}
        className="mb-gutter-md text-signal-sm font-semibold uppercase tracking-wider text-text-secondary"
      >
        {label}
      </h3>
      <div className="flex flex-col">
        {steps.map((step, idx) => (
          <FlowStepCard
            key={step.id}
            step={step}
            isLast={idx === steps.length - 1}
          />
        ))}
      </div>
    </section>
  );
}

function EngineCardRow({ card }: { card: EngineCard }) {
  const headingId = `engine-heading-${card.id}`;
  return (
    <article
      aria-labelledby={headingId}
      data-testid={`engine-card-${card.id}`}
      className="rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-gutter-md">
        <div className="flex items-center gap-gutter-md">
          <span aria-hidden="true" className="text-2xl leading-none">
            {card.emoji}
          </span>
          <h3
            id={headingId}
            className="text-signal-base font-semibold text-text-primary"
          >
            {card.title}
          </h3>
        </div>
        <StatusBadge status={card.status} note={card.statusNote} />
      </div>
      <p className="mt-gutter-sm text-signal-sm text-text-secondary">{card.description}</p>
      {card.showJsonBlock && (
        <div
          className="mt-gutter-md"
          data-testid="llm-json-schema-block"
          aria-label="LLM structured output JSON schema"
        >
          <p className="mb-gutter-sm text-signal-xs font-semibold uppercase tracking-wider text-text-tertiary">
            Structured output schema
          </p>
          <pre className="overflow-x-auto rounded-signal-md bg-surface-muted p-gutter-md text-signal-xs text-text-secondary ring-1 ring-inset ring-surface-border">
            <code>{LLM_JSON_SCHEMA}</code>
          </pre>
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

/**
 * ArchitectureTab - renders the data flow diagram (2-column) plus the
 * 5-engine status block, matching the canonical prototype layout.
 *
 * Consumed by `src/views/Architecture.tsx`. Extracted as a standalone
 * component so it can be tested and snapshot-diffed independently of the
 * view shell.
 */
export function ArchitectureTab() {
  return (
    <div
      role="region"
      aria-label="Clinical AI Architecture data flow diagram for the Cardiometabolic Pathway"
      data-testid="architecture-tab"
      className="mx-auto max-w-5xl space-y-gutter-xl"
    >
      {/* Section header */}
      <div>
        <h2 className="text-signal-xl font-semibold text-text-primary">
          Clinical AI Architecture for the Cardiometabolic Pathway
        </h2>
        <p className="mt-gutter-sm text-signal-sm text-text-secondary">
          Two input streams -- same Rule Engine + Pathway Engine + LLM -- outputs to NBA (clinician) and JITAI (patient)
        </p>
      </div>

      {/* 2-column flow diagram */}
      <div
        role="region"
        aria-label="Two-column data flow diagram"
        data-testid="flow-diagram"
        className="grid grid-cols-1 gap-gutter-xl md:grid-cols-2"
      >
        <StreamColumn
          label="Stream 1: Patient-Reported Data"
          streamId="stream-1"
          steps={STREAM1_STEPS}
        />
        <StreamColumn
          label="Stream 2: Clinical Event Data"
          streamId="stream-2"
          steps={STREAM2_STEPS}
        />
      </div>

      {/* Engine cards */}
      <div
        role="region"
        aria-label="AI engine status cards"
        data-testid="engine-cards-section"
        className="space-y-gutter-md"
      >
        <h3 className="text-signal-base font-semibold text-text-primary">
          Engine Status
        </h3>
        {ENGINE_CARDS.map((card) => (
          <EngineCardRow key={card.id} card={card} />
        ))}
      </div>
    </div>
  );
}

export { STREAM1_STEPS, STREAM2_STEPS, ENGINE_CARDS };
